import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import {
  ScrollView,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ScrollViewProps,
} from 'react-native';

import { useReducedMotion } from '../useReducedMotion';
import { useDockHeight } from './LoggerDock';
import { useLoggerContext } from './loggerContext';

/**
 * LG-98 — the logger's scroller, plus the one thing it could not do: bring the
 * row the athlete is typing into back above the dock.
 *
 * Antenore on the dev 4.66.3 (2026-09-14): «quando tappo sul peso e ci sono
 * timer e banda aperti… me lo copre e quindi devo scrollare per vedere la linea
 * con reps e peso». Since LG-95 the load band is permanent and the rest timer
 * raises itself, so the absolute dock (LoggerDock.tsx:233) is tall exactly when
 * the keyboard is up — and `adjustResize` shrinks the window under a row that
 * does not move.
 *
 * ⛔ The spacer is NOT this. `LoggerDockSpacer` reserves the dock's height at
 * the END of the scroller, which protects the last card; the focus row sits at
 * the TOP and was always reachable — nobody was taking the athlete there.
 *
 * ⚠️⚠️ The cure is more dangerous than the defect, so the floor from
 * `timeline-logger.md#lg-98` is encoded here and each rule says which line
 * keeps it:
 *  - scroll ONLY when the row is really covered, by exactly the covered amount
 *    — ⛔ never "centre it", the 1.69.1 mistake (changelog-0.54-3.78.md:369);
 *  - ONLY while a logger field holds the keyboard (`anyFieldFocused`);
 *  - ⛔ NEVER in reaction to a dock-height change — the dock breathes on its own
 *    during a rest, and an athlete watching the screen must not see it move.
 *    That is why every value the maths needs is read through a ref: nothing
 *    here is an effect dependency;
 *  - the athlete taking the scroll DISARMS it until the next focus;
 *  - reduced motion drops the animation (the house hook, as ExerciseVisualPager
 *    already does on its own scrollTo).
 *
 * ⚠️ One deliberate narrowing of the written floor: it says "a touch disarms",
 * this disarms on `onScrollBeginDrag` — a drag, not any touch. `onTouchStart`
 * fires on the way INTO a field too, so it would disarm the reveal a beat
 * before the focus that asks for it. A tap that is not a drag either focuses a
 * field (which re-arms) or changes nothing.
 *
 * 🪤 The measurement is taken TWICE on purpose. At `onFocus` the soft keyboard
 * has not come up yet, so the first answer is about a window that is about to
 * shrink; the scroller's own `onLayout` fires when `adjustResize` shrinks it,
 * and that is when the real answer arrives. Measuring once was the 1.68.8
 * mistake in a different costume: reading the layout before the reflow.
 */

type RegisterRow = (node: View | null) => void;

/** A measured rectangle in screen coordinates, as `measureInWindow` hands it. */
export type ScreenRect = { y: number; height: number };

/**
 * How much of `row` the dock is eating, in pixels. ⭐ Zero or less means
 * VISIBLE — and visible means the screen does not move.
 *
 * Pure and exported because it is the whole decision: the rest of this file is
 * plumbing for when to ask it, and a rule that decides whether the screen moves
 * under an athlete's eyes should be checkable without a phone.
 */
export function hiddenBehindDock(
  row: ScreenRect,
  scroller: ScreenRect,
  dockHeight: number,
  gap: number,
): number {
  // A scroller of no height has not been laid out yet: nothing is known, so
  // nothing is covered. ⛔ Guessing here would scroll on the strength of a
  // measurement that does not exist.
  if (scroller.height <= 0 || row.height <= 0) return 0;
  const floor = scroller.y + scroller.height - dockHeight;
  return row.y + row.height + gap - floor;
}

/**
 * Is THIS layout change the keyboard coming up?
 *
 * ⭐ The second look only exists because `adjustResize` shrinks the window
 * after `onFocus` has already measured. But `onLayout` fires for every reason
 * a frame can change — a desktop window dragged narrower on web-rnw, a phone
 * rotated mid-set — and re-revealing on those moves the screen for something
 * the athlete did NOT do by tapping a field. So the trigger is not "the layout
 * changed", it is "the scroller got SHORTER", which is what a keyboard does
 * and a rotation into landscape does not.
 *
 * The first layout (`previous === 0`) is never a shrink: there is nothing to
 * have shrunk FROM, and the focus effect has already asked once.
 */
export function scrollerShrank(previous: number, next: number): boolean {
  return previous > 0 && next > 0 && next < previous;
}

const RevealRowContext = createContext<RegisterRow>(() => {});

/**
 * Leaf side — put this on the row that must stay visible. One row at a time:
 * the timeline logger mounts exactly one FocusPanel, and a second mount would
 * simply take the slot (the ref callback clears it again on unmount).
 */
export function useRevealRowRef(): RegisterRow {
  return useContext(RevealRowContext);
}

/**
 * Owner side — replaces the plain `<ScrollView>` of the timeline logger. It
 * lives INSIDE `LoggerDockProvider` because that is the only place the dock's
 * height can be read, and it renders the scroller itself so the sibling order
 * around it (load-bearing, LoggerDock.tsx:188-193) is untouched.
 */
export function LoggerRevealScroller({
  children,
  ...scrollProps
}: ScrollViewProps & { children: ReactNode }) {
  const { fieldFocus, theme } = useLoggerContext();
  const { anyFieldFocused } = fieldFocus;
  const dockHeight = useDockHeight();
  const reducedMotion = useReducedMotion();

  const scrollRef = useRef<ScrollView>(null);
  const rowRef = useRef<View | null>(null);
  // Live values the maths reads at call time. ⛔ Refs, not dependencies: a
  // dock that grows mid-rest must never BE the reason a scroll happens.
  const dock = useRef(dockHeight);
  dock.current = dockHeight;
  const motion = useRef(reducedMotion);
  motion.current = reducedMotion;
  // A breath between the row and the dock, so the row is clear of it rather
  // than flush against it. Token, never a constant.
  const gap = useRef(theme.spacing.s3);
  gap.current = theme.spacing.s3;
  // The caller's own handlers, read at call time: the props object is rebuilt
  // every render, so depending on it would rebuild three callbacks a frame.
  const passthrough = useRef(scrollProps);
  passthrough.current = scrollProps;
  // Where the scroller is right now. ScrollView has no scrollBy, so the
  // relative correction has to be added to an absolute offset. Written to a
  // ref, so tracking it costs no render.
  const offset = useRef(0);
  const armed = useRef(false);
  // The scroller's last known height, so a layout change can be asked WHY it
  // happened instead of being obeyed.
  const height = useRef(0);

  const registerRow = useCallback<RegisterRow>((node) => {
    rowRef.current = node;
  }, []);

  const reveal = useCallback(() => {
    if (!armed.current) return;
    const scroller = scrollRef.current;
    const row = rowRef.current;
    // ⚠️ Optional CALL, not just optional access: this ships to web-rnw too,
    // and react-native-web is where an RN method quietly turns out not to
    // exist ([[reference_rnw_silently_drops_rn_a11y_props]]). Both halves were
    // checked in the installed versions — RN 0.85.3 ScrollView.d.ts:888 and
    // RNW 0.21.2 ScrollView/index.js:201, whose node gets `measureInWindow`
    // from usePlatformMethods — so this guard is insurance, not a workaround.
    const native = scroller?.getNativeScrollRef?.();
    if (scroller == null || row == null || native == null) return;
    // The method from changelog-0.54-3.78.md:362 — measure both in SCREEN
    // coordinates, then correct by the difference. ⛔ Not its constants: the
    // two attempts that shipped with those were both wrong (:368-369).
    native.measureInWindow((_sx, scrollerY, _sw, scrollerH) => {
      if (!armed.current) return;
      row.measureInWindow((_rx, rowY, _rw, rowH) => {
        // Re-check: a measurement is a round trip, and the athlete may have
        // grabbed the list while it was in flight.
        if (!armed.current) return;
        const hidden = hiddenBehindDock(
          { y: rowY, height: rowH },
          { y: scrollerY, height: scrollerH },
          dock.current,
          gap.current,
        );
        // Visible already: do nothing at all. This is the whole difference
        // between a cure and a screen that moves under the athlete's eyes.
        if (hidden <= 0) return;
        scroller.scrollTo({
          y: offset.current + hidden,
          animated: !motion.current,
        });
      });
    });
  }, []);

  // Rising edge of the keyboard signal, and only that. Reps → weight inside
  // the same row does not flip it (the LG-72 hand-over grace), which is
  // correct: both cells are the SAME row, already revealed by the first focus.
  useEffect(() => {
    armed.current = anyFieldFocused;
    if (anyFieldFocused) reveal();
  }, [anyFieldFocused, reveal]);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      passthrough.current.onLayout?.(e);
      // The second look — but ONLY when this layout is the keyboard, i.e. the
      // scroller got shorter. A window resized on web or a phone rotated is
      // also an onLayout, and neither is a reason to move the page under the
      // athlete (found by the a11y audit, 2026-09-14).
      const next = e.nativeEvent.layout.height;
      const shrank = scrollerShrank(height.current, next);
      height.current = next;
      if (shrank) reveal();
    },
    [reveal],
  );

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      offset.current = e.nativeEvent.contentOffset.y;
      passthrough.current.onScroll?.(e);
    },
    [],
  );

  const onScrollBeginDrag = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      // The athlete took the scroll. It is theirs until the next focus.
      armed.current = false;
      passthrough.current.onScrollBeginDrag?.(e);
    },
    [],
  );

  return (
    <RevealRowContext.Provider value={registerRow}>
      <ScrollView
        {...scrollProps}
        ref={scrollRef}
        onLayout={onLayout}
        onScroll={onScroll}
        onScrollBeginDrag={onScrollBeginDrag}
        scrollEventThrottle={16}
      >
        {children}
      </ScrollView>
    </RevealRowContext.Provider>
  );
}
